import { useEffect, useState } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  TextInput,
  NumberInput,
  Button,
  FileButton,
  Box,
  ThemeIcon,
  Switch,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconWorld,
  IconAt,
  IconShoe,
  IconPhoto,
  IconSettings,
  IconHeartStar,
} from '@tabler/icons-react';
import { useApi, useApiClient, mediaUrl } from '../../api';
import { uploadSellerBanner } from '../../api/uploads';

// Public-profile extras editor (contract §3.5): website, social handles,
// shoe size via PATCH /me/seller; banner via POST /uploads/seller-banner.
const EXTRA_FIELDS = ['website_url', 'instagram_handle', 'twitter_handle', 'tiktok_handle', 'shoe_size'];

// Fan subscription price bounds (contract §4.19: 299–9999 cents, null = disabled)
const FAN_SUB_MIN_DOLLARS = 2.99;
const FAN_SUB_MAX_DOLLARS = 99.99;
const FAN_SUB_RANGE_MESSAGE = `Price must be between $${FAN_SUB_MIN_DOLLARS} and $${FAN_SUB_MAX_DOLLARS}`;

const SellerProfileSettings = () => {
  const api = useApiClient();
  const { data: profile, refetch } = useApi('/api/v1/me/seller');

  const [form, setForm] = useState({
    website_url: '',
    instagram_handle: '',
    twitter_handle: '',
    tiktok_handle: '',
    shoe_size: '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Vacation mode (contract §4.3): is_active_seller = profile visible to buyers.
  // PATCH is approved-only, so the switch is hidden until approved + verified.
  const [profileVisible, setProfileVisible] = useState(true);
  const isApproved = profile?.seller_status === 'approved' && !!profile?.is_verified;

  // Fan subscription (contract §4.19): fan_sub_price_cents int|null on
  // SellerPrivate, writable via PATCH /me/seller (null disables).
  const [fanSubEnabled, setFanSubEnabled] = useState(false);
  const [fanSubDollars, setFanSubDollars] = useState('');
  const [savingFanSub, setSavingFanSub] = useState(false);

  // Seed the form from the loaded profile
  useEffect(() => {
    if (!profile) return;
    setForm(Object.fromEntries(EXTRA_FIELDS.map((f) => [f, profile[f] ?? ''])));
    setProfileVisible(profile.is_active_seller ?? true);
    setFanSubEnabled(profile.fan_sub_price_cents != null);
    setFanSubDollars(profile.fan_sub_price_cents != null ? profile.fan_sub_price_cents / 100 : '');
  }, [profile]);

  const setField = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.currentTarget.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Cleared fields are sent as explicit null (contract §3.5: null clears)
      await api.patch(
        '/api/v1/me/seller',
        Object.fromEntries(EXTRA_FIELDS.map((f) => [f, form[f].trim() || null]))
      );
      notifications.show({ color: 'green', message: 'Public profile updated' });
      refetch();
    } catch (err) {
      notifications.show({ color: 'red', message: err.message || 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  // Immediate PATCH (not part of Save); optimistic with revert on failure
  const handleVisibilityToggle = async (event) => {
    const next = event.currentTarget.checked;
    const previous = profileVisible;
    setProfileVisible(next);
    try {
      await api.patch('/api/v1/me/seller', { is_active_seller: next });
      notifications.show({
        color: 'green',
        message: next
          ? 'Your profile is visible to buyers again'
          : 'Your profile is now hidden from buyers',
      });
      refetch();
    } catch (err) {
      setProfileVisible(previous);
      notifications.show({ color: 'red', message: err.message || 'Failed to update visibility' });
    }
  };

  const fanSubAmount =
    typeof fanSubDollars === 'number' ? fanSubDollars : parseFloat(fanSubDollars);
  const fanSubCents = Math.round(fanSubAmount * 100);
  const fanSubValid =
    Number.isFinite(fanSubAmount) &&
    fanSubCents >= Math.round(FAN_SUB_MIN_DOLLARS * 100) &&
    fanSubCents <= Math.round(FAN_SUB_MAX_DOLLARS * 100);

  // Turning the switch off disables immediately (PATCH null, revert on
  // failure); turning it on just reveals the price input — nothing is
  // enabled server-side until a valid price is saved.
  const handleFanSubToggle = async (event) => {
    const next = event.currentTarget.checked;
    if (next) {
      setFanSubEnabled(true);
      return;
    }
    setFanSubEnabled(false);
    if (profile?.fan_sub_price_cents == null) return; // was never enabled server-side
    try {
      await api.patch('/api/v1/me/seller', { fan_sub_price_cents: null });
      notifications.show({ color: 'green', message: 'Fan subscription disabled' });
      refetch();
    } catch (err) {
      setFanSubEnabled(true);
      notifications.show({ color: 'red', message: err.message || 'Failed to disable fan subscription' });
    }
  };

  const handleFanSubSave = async () => {
    if (!fanSubValid || savingFanSub) return;
    setSavingFanSub(true);
    try {
      await api.patch('/api/v1/me/seller', { fan_sub_price_cents: fanSubCents });
      notifications.show({ color: 'green', message: 'Fan subscription price saved' });
      refetch();
    } catch (err) {
      notifications.show({ color: 'red', message: err.message || 'Failed to save fan subscription price' });
    } finally {
      setSavingFanSub(false);
    }
  };

  const handleBannerUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadSellerBanner(api, file);
      notifications.show({ color: 'green', message: 'Banner updated' });
      refetch();
    } catch (err) {
      notifications.show({ color: 'red', message: err.message || 'Banner upload failed' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card withBorder radius="md" padding="md">
      <Stack gap="md">
        <Group gap="xs">
          <ThemeIcon size="sm" color="grape" variant="light" radius="xl">
            <IconSettings size="0.8rem" />
          </ThemeIcon>
          <Text fw={600} size="sm">Public Profile</Text>
        </Group>

        {/* Vacation mode (approved sellers only — PATCH is approved-gated) */}
        {isApproved && (
          <Switch
            color="grape"
            label="Profile visible to buyers"
            description="Turn off to hide your profile, products and gallery while you're away"
            checked={profileVisible}
            onChange={handleVisibilityToggle}
          />
        )}

        {/* Fan subscription (approved sellers only — PATCH is approved-gated) */}
        {isApproved && (
          <Stack gap="xs">
            <Switch
              color="grape"
              label="Fan subscription"
              description="Let fans subscribe monthly to unlock your private gallery"
              checked={fanSubEnabled}
              onChange={handleFanSubToggle}
              thumbIcon={<IconHeartStar size="0.7rem" />}
            />
            {fanSubEnabled && (
              <Group align="flex-end" gap="sm" wrap="nowrap">
                <NumberInput
                  label="Monthly price (USD)"
                  value={fanSubDollars}
                  onChange={setFanSubDollars}
                  min={FAN_SUB_MIN_DOLLARS}
                  max={FAN_SUB_MAX_DOLLARS}
                  decimalScale={2}
                  prefix="$"
                  error={fanSubDollars !== '' && !fanSubValid ? FAN_SUB_RANGE_MESSAGE : null}
                  style={{ maxWidth: 200 }}
                />
                <Button
                  variant="light"
                  color="grape"
                  onClick={handleFanSubSave}
                  loading={savingFanSub}
                  disabled={!fanSubValid || fanSubCents === profile?.fan_sub_price_cents}
                >
                  Save price
                </Button>
              </Group>
            )}
          </Stack>
        )}

        {/* Banner */}
        <Stack gap="xs">
          <Text size="xs" c="dimmed">Profile banner</Text>
          <Box
            style={{
              height: 120,
              borderRadius: 8,
              overflow: 'hidden',
              backgroundColor: '#f8f9fa',
              border: '1px dashed #ced4da',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {profile?.banner_image_path ? (
              <img
                src={mediaUrl(profile.banner_image_path)}
                alt="Profile banner"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Text c="dimmed" size="sm">No banner yet</Text>
            )}
          </Box>
          <FileButton onChange={handleBannerUpload} accept="image/*">
            {(props) => (
              <Button
                {...props}
                variant="light"
                color="grape"
                size="xs"
                loading={uploading}
                leftSection={<IconPhoto size="0.9rem" />}
                style={{ alignSelf: 'flex-start' }}
              >
                {profile?.banner_image_path ? 'Change banner' : 'Upload banner'}
              </Button>
            )}
          </FileButton>
        </Stack>

        {/* Links + facts */}
        <TextInput
          label="Website"
          placeholder="https://…"
          leftSection={<IconWorld size="0.9rem" />}
          value={form.website_url}
          onChange={setField('website_url')}
        />
        <Group grow>
          <TextInput
            label="Instagram"
            placeholder="yourhandle"
            leftSection={<IconAt size="0.9rem" />}
            value={form.instagram_handle}
            onChange={setField('instagram_handle')}
          />
          <TextInput
            label="Twitter / X"
            placeholder="yourhandle"
            leftSection={<IconAt size="0.9rem" />}
            value={form.twitter_handle}
            onChange={setField('twitter_handle')}
          />
          <TextInput
            label="TikTok"
            placeholder="yourhandle"
            leftSection={<IconAt size="0.9rem" />}
            value={form.tiktok_handle}
            onChange={setField('tiktok_handle')}
          />
        </Group>
        <TextInput
          label="Shoe size"
          placeholder="e.g. 7.5"
          leftSection={<IconShoe size="0.9rem" />}
          value={form.shoe_size}
          onChange={setField('shoe_size')}
          maxLength={10}
          style={{ maxWidth: 160 }}
        />

        <Button
          color="grape"
          size="sm"
          onClick={handleSave}
          loading={saving}
          style={{ alignSelf: 'flex-start' }}
        >
          Save public profile
        </Button>
      </Stack>
    </Card>
  );
};

export default SellerProfileSettings;
